import { useCallback, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { IconFolder } from './Icon';

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = /^image\/(png|jpeg|webp)$/i;

export interface UploadWidgetProps {
  uploading: boolean;
  progress: number;
  onPick: (file: File) => void;
  /** Friendly error message to surface underneath the drop zone. */
  errorMessage?: string | null;
}

export function UploadWidget(props: UploadWidgetProps) {
  const { uploading, progress, onPick, errorMessage } = props;
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = useCallback((file: File): string | null => {
    if (!ALLOWED.test(file.type)) {
      return "That file type isn't supported — try PNG, JPG, or WebP.";
    }
    if (file.size > MAX_BYTES) {
      return 'That file is too big — max 10 MB.';
    }
    return null;
  }, []);

  const accept = useCallback(
    (file: File) => {
      const err = validate(file);
      if (err) {
        setLocalError(err);
        return;
      }
      setLocalError(null);
      onPick(file);
    },
    [onPick, validate],
  );

  const handleFile = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) accept(file);
      // Reset so picking the same file again still fires onChange.
      e.target.value = '';
    },
    [accept],
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) accept(file);
    },
    [accept],
  );

  const open = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const visibleError = localError ?? errorMessage ?? null;

  return (
    <div className="flex flex-col gap-3">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={[
          'flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed p-10 text-center motion-safe:transition-colors',
          dragging
            ? 'border-accent bg-accent/10'
            : 'border-neutral-300 bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-900',
        ].join(' ')}
      >
        <span aria-hidden="true" className="text-neutral-400 dark:text-neutral-500">
          <IconFolder size={36} aria-hidden />
        </span>
        <div>
          <p className="text-base font-semibold text-neutral-900 dark:text-neutral-50">
            Drop your artwork here
          </p>
          <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">
            PNG, JPG, or WebP — up to 10 MB
          </p>
        </div>
        <button
          type="button"
          onClick={open}
          disabled={uploading}
          aria-busy={uploading}
          className="inline-flex h-11 cursor-pointer items-center justify-center rounded-md bg-accent px-5 text-sm font-semibold text-neutral-950 motion-safe:transition-colors hover:bg-accent-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-50 disabled:cursor-not-allowed disabled:opacity-60 dark:focus-visible:ring-offset-neutral-950"
        >
          {uploading ? `Uploading… ${progress}%` : 'Choose a file'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleFile}
          aria-label="Upload artwork image"
          className="sr-only"
        />
      </div>
      {uploading ? (
        <div
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Upload progress"
          aria-valuetext={`${progress}% uploaded`}
          className="h-2 w-full overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800"
        >
          <div
            className="h-full bg-accent motion-safe:transition-[width]"
            style={{ width: `${progress}%` }}
          />
        </div>
      ) : null}
      {visibleError ? (
        <p role="alert" className="text-sm text-rose-600 dark:text-rose-400">
          {visibleError}
        </p>
      ) : null}
    </div>
  );
}
